import { Box, ListItem, ListItemButton, Stack } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { isSpecial } from "routes/schedule/tree/schedules/isSpecial"
import { ListItemBox } from "lib/@components/ListItemBox"
import { FolderItemPart } from "routes/schedule/tree/schedules/folder-item-part"
import { Selected } from "routes/schedule/components/selected"
import {
    ScheduleItemBelow,
    ScheduleItemBeneath,
    ScheduleItemCode,
    ScheduleItemIcon,
    ScheduleItemMain,
    ScheduleItemMainExtra,
    ScheduleItemRight,
    ScheduleItemWrapper,
} from "slot-definitions"
import { Bound } from "lib/@components/binding/Bound"
import { OpenSchedule } from "event-definitions"
import { navigate } from "lib/routes/navigate"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { ScheduleEmbed } from "lib/@components/schedule-embed"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import { ScheduleCode } from "routes/regime/schedule-code"
import { RegimeDraftLockHide } from "routes/regime/regime-draft-locking/regime-draft-lock-hide"
import { handleUnlicensedContent } from "routes/schedule/lib/handle-unlicensed-content"
import { ScheduleSummaryTooltip } from "routes/schedule/plugins/ai-summary/summary"
import { uniqueId } from "library/database/split-id.mjs"
import { prevent } from "lib/prevent"
import noop from "lib/noop"

ScheduleItemCode.plug(
    <ScheduleEmbed>
        <Retired />
    </ScheduleEmbed>
)

function Retired({ schedule }) {
    return schedule?.retired ? "Retired" : null
}

export function ScheduleItem({ schedule, extra = null, last, end = null, start = null }) {
    const { onClickSchedule: open = openSchedule, target } = useBoundContext()
    const summaryTooltipId = `schedule-summary-${uniqueId(schedule._id)}`
    return (
        <Bound schedule={schedule} currentId={target.id} summaryTooltipId={summaryTooltipId}>
            <ScheduleItemWrapper.Wrapper>
                <ListItem
                    data-cy="schedule-item"
                    className="schedule-item"
                    sx={{
                        py: 0,
                        color: schedule.notLicensed || schedule.$?.notLicensed ? "unlicensed.dark" : undefined,
                    }}
                    divider={!last}
                    component="div"
                >
                    <Stack alignItems="stretch" sx={{ width: 1 }}>
                        <ListItemBox spacing={1} width={1} justifyContent={"space-between"}>
                            <Stack sx={{ minWidth: 0 }} width={1}>
                                <ListItemBox spacing={1}>
                                    <RegimeDraftLockHide>
                                        <FolderItemPart sx={{ minWidth: 32 }}>
                                            <Selected />
                                        </FolderItemPart>
                                    </RegimeDraftLockHide>
                                    <ListItemButton
                                        data-cy="schedule-item-button"
                                        disabled={schedule.isDisabled}
                                        sx={{ px: 0, py: 0.5, m: 0 }}
                                        onClick={() => open(schedule)}
                                        aria-labelledby={summaryTooltipId}
                                        onFocus={showSummaryTooltip}
                                        onBlur={hideSummaryTooltip}
                                        onMouseDown={prevent(noop)}
                                    >
                                        <Stack spacing={0} sx={{ width: 1 }}>
                                            <ListItemBox>
                                                {!!start && <Box flexShrink={1}>{start}</Box>}
                                                <Box
                                                    data-cy="schedule-item-icon"
                                                    sx={{
                                                        fontWeight: isSpecial(schedule.label) ? "bold" : undefined,
                                                        mr: 1,
                                                        lineHeight: 0,
                                                    }}
                                                >
                                                    <ScheduleItemIcon.Slot schedule={schedule}>
                                                        <Iconify
                                                            icon="mdi:file-document-outline"
                                                            sx={{ width: 32, height: 32 }}
                                                        />
                                                    </ScheduleItemIcon.Slot>
                                                </Box>
                                                <Stack data-cy="schedule-item-main" sx={{ overflow: "hidden" }}>
                                                    <ScheduleItemMain.Slot schedule={schedule}>
                                                        <Box
                                                            flex={1}
                                                            overflow={"hidden"}
                                                            sx={{
                                                                fontWeight: isSpecial(schedule.label)
                                                                    ? "bold"
                                                                    : undefined,
                                                            }}
                                                        >
                                                            <Box display="flex" alignItems="center">
                                                                <TruncatedTypography data-cy="schedule-item-title">
                                                                    {schedule.label ?? schedule.title}
                                                                </TruncatedTypography>
                                                                <ScheduleItemMainExtra.Slot schedule={schedule} />
                                                            </Box>
                                                            {extra}
                                                        </Box>
                                                    </ScheduleItemMain.Slot>
                                                </Stack>
                                            </ListItemBox>
                                            <ScheduleItemBelow.Slot schedule={schedule} />
                                        </Stack>
                                    </ListItemButton>
                                </ListItemBox>
                            </Stack>
                            <Stack data-cy="schedule-item-extra-details">
                                <ListItemBox spacing={1}>
                                    <ScheduleItemCode.Slot schedule={schedule}>
                                        <ScheduleCode code={schedule.code} dataCy="schedule-item-code" />
                                    </ScheduleItemCode.Slot>
                                    <ScheduleItemRight.Slot schedule={schedule} />

                                    {!!end && <Box>{end}</Box>}
                                </ListItemBox>
                            </Stack>
                        </ListItemBox>
                        <ScheduleItemBeneath.Slot schedule={schedule} />
                    </Stack>
                </ListItem>
            </ScheduleItemWrapper.Wrapper>
        </Bound>
    )

    function openSchedule() {
        if (handleUnlicensedContent(schedule)) return
        OpenSchedule(schedule.id).raiseAsync(schedule, {}, { version: schedule.version })
    }

    function showSummaryTooltip() {
        ScheduleSummaryTooltip(schedule?._id).raise({ open: true })
    }

    function hideSummaryTooltip() {
        ScheduleSummaryTooltip(schedule?._id).raise({ open: false })
    }
}

OpenSchedule("*").after.handleOnce((schedule, params = {}, options = {}) => {
    const output = Object.entries(params)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join("&")
    const ampersandVersion = output ? "&" : ""
    navigate(
        `/app/schedules/${schedule.id}?${output}${
            options.version ? `${ampersandVersion}version=${options.version}` : ""
        }`,
        options
    )
})
